<template>
  <div>
    <h2>
      <span class="form-group">
        Mastitis/Milk Quality
        <comments field-name='heading_mastitis_milk_quality' :isReadOnly="isReadOnly"></comments>
      </span>
    </h2>

    <h3 class="text-left">
      <span class="form-group">
        Dry Cow Sales
        <comments field-name='heading_dry_cow_used' :isReadOnly="isReadOnly"></comments>
      </span>
    </h3>
    <p class="text-left"> Product quantities purchased by season.</p>
    <table class="table table-bordered">
      <thead>
        <tr>
          <th>Animal Name</th>
          <th>Product</th>
          <th>{{ seasonStartYear-2 }}-{{ (seasonStartYear -1).toString().substr(2, 2) }}</th>
          <th>{{ seasonStartYear-1 }}-{{ (seasonStartYear).toString().substr(2, 2) }}</th>
        </tr>
      </thead>
      <tbody>
        <tr v-for="(sale, index) in getChartData('dc_get_drycow_sales')" :key="'gds_'+index">
          <td class="text-left">{{ sale["Animal name"] }}</td>
          <td class="text-left">{{ sale["Product"] }}</td>
          <td class="text-left">{{ sale["qty_ls"] }}</td>
          <td class="text-left">{{ sale["qty_ts"] }}</td>
        </tr>
      </tbody>
    </table>

    <div class="clearfix" :style="{ width: widthForRow(getFieldValue('FARMS')) + 'px' }">
      <div v-for="farm in getFieldValue('FARMS')" class="farm-fields" :key="'farm_'+farm.id">
        <h3>{{ farm.farm_label }}</h3>

        <h3 class="text-left"> 
          <span class="form-group">
            Actual Dry Cow Strategy
            <comments 
              field-name="heading_actual_dry_cow_strategy" 
              :farm-id="farm.id" 
              :isReadOnly="isReadOnly">
            </comments>
          </span>
        </h3>
        <br>

        <text-area label="Key points to revisit from Autumn Milk Quality Review"
                   field-name="milk_quality_key_points"
                   :farm-id="farm.id"
                   :disabled='isReadOnly'>
        </text-area>

        <span class="form-group">
          Did you have any deaths or sickness post dry-off?
          <comments field-name='post_dry_off_sickness_comments' :farm-id="farm.id" :isReadOnly="isReadOnly"></comments>
        </span>
      </div>
    </div>

    <h3 class="text-left"> 
      <span class="form-group">
        Seasonal Mastitis
        <comments field-name='heading_chart_mastitis_milk_quality' :isReadOnly="isReadOnly"></comments>
      </span>
    </h3>
    <table class="table table-bordered">
      <thead>
        <tr>
          <th>Farm</th>
          <th>Season</th>
          <th>Cows Treated</th>
          <th>Percent Cows Wintered</th>
        </tr>
      </thead>
      <tbody>
        <tr v-for="(smmq, index) in getChartData('seasonal_mastitis')" :key="'sm_'+index">
          <td class="text-left">{{ smmq[0] }}</td>
          <td class="text-left">{{ smmq[1] }}</td>
          <td class="text-left">{{ smmq[2] }}</td>
          <td class="text-left">{{ smmq[3] }}</td>
        </tr>
      </tbody>
    </table>

    <div v-for="farm in getFieldValue('FARMS')" :key="'scmi_farm_'+farm.id">
      <div :class="{fonterraFields: isFonterraClient && isFonterraFarm()(farm.id)}">
        <h3>
          <span class="form-group">
            {{farm.farm_label}} season total clinical mastitis incidence
          </span>
        </h3>
        <table class="table table-bordered">
          <thead>
            <tr>
              <th>Season</th>
              <th>Farm</th>
              <th>Cows Treated</th>
              <th>Percent Cows Wintered</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="(scmi, index) in getChartData('cd_season_mastitis_percent')[farm.id]" :key="'scmi_'+index">
              <td class="text-left">{{ scmi['Season'] }}</td>
              <td class="text-left">{{ scmi['Farm'] }}</td>
              <td class="text-left">{{ scmi['Cows Treated'] }}</td>
              <td class="text-left">{{ scmi['Percent Cows Wintered'] }}</td>
            </tr>
          </tbody>
        </table>

        <text-input label="Target % of clinical mastitis cases"
                    field-name="cd_season_clinical_mastitis_incidence"
                    :farm-id="farm.id"
                    :disabled="isReadOnly">
        </text-input>

        <div class="form-group">
          <label>Plan for Clinical Mastitis Rate</label>
          <comments :field-name='`farm_${farm.id}-bcs_mastitis_rate_plan`' :isReadOnly="isReadOnly"></comments>
        </div>
      </div>

    </div>

    <h3 class="text-left">
      <span class="form-group">
        Mastitis Treatments
        <comments field-name='heading_mastitis_treatments' :isReadOnly="isReadOnly"></comments>
      </span>
    </h3>
    <div v-for="farm in getFieldValue('FARMS')" :key="'mt_farm_'+farm.id">
      <h4> {{ farm.farm_label }}
        <span class="form-group">
          <comments :field-name="`farm_${farm.id}-chart_mastitis_treatments`" :isReadOnly="true">
          </comments>
        </span>
      </h4>
      <p>Estimated number of cows treated per month based on purchases.</p>
      <div class="step-section">
        <bar :chart-data="treatmentsChartData(farm.id)" :options="treatmentsChartOptions" ></bar>

        <table class="table table-bordered">
          <thead>
            <tr>
              <th></th>
              <th v-for="(date, index) in chartMastitisTreatmentsDates(farm.id)" :key="'mt_'+index"> {{ date }}</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="(product, productIndex) in chartMastitisTreatmentsProducts(farm.id)" :key="'cmmp_'+productIndex">
              <td>{{ product }}</td>
              <td v-for="(date, dateIndex) in chartMastitisTreatmentsDates(farm.id)" :key="farm.id+'_dateindex_'+dateIndex">
                {{ getProductValueForDate(farm.id, product, date) || '' }}
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>

    <div class="clearfix" :style="{ width: widthForRow(getFieldValue('FARMS')) + 'px' }">
      <div v-for="farm in getFieldValue('FARMS')" class="farm-fields" :key="'farm_fields_'+farm.id">
        <h3>{{ farm.farm_label }}</h3>
        <number-input label="Avg. BMSCC Last Season"
                      :field-name="`farm_${farm.id}-mmq_bmscc`"
                      :disabled="isReadOnly">
        </number-input>
      </div>
    </div>

    <drug-allocation-cart :farms="getFieldValue('FARMS')" :drugs="getFieldValue('DRUGS')" :isReadOnly="isReadOnly">
    </drug-allocation-cart>
  </div>
</template>

<script>
import { mapGetters } from 'vuex'
import baseComponent from '@/mixins/base_consult_component_v2'
import DrugAllocationCart from './DrugAllocationCart'
import Bar from '../charts/Bar'

export default {
  name: 'MastitisMilkQuality',
  mixins: [ baseComponent ],
  components: {
    DrugAllocationCart,
    Bar,
  },
  created() {
    this.colors = []
    for (let i = 1; i <= 10; i++) {
      this.colors.push('#'+(Math.random()*0xFFFFFF<<0).toString(16))
    }
  },
  computed: {
    ...mapGetters('currentConsult', ['isFonterraClient']),
    treatmentsChartOptions: function() {
      return {
        responsive: true,
        scales: {
          xAxes: [{
            stacked: true,
            categoryPercentage: 0.5,
            barPercentage: 1
          }],
          yAxes: [{
            stacked: true
          }]
        }
      }
    },
  },
  methods: {
    ...mapGetters('currentConsult', ['isFonterraFarm']),
    percentCowsWintered: function(farmId, cowsTreated)
    {
      let fieldName = `farm_${farmId}-cows_wintered_incl_r2`
      let totalCowsWintered = this.getFieldValue(fieldName, 'AnimalNumbers')

      if (totalCowsWintered && totalCowsWintered > 0)
      {
        let percent = (cowsTreated / totalCowsWintered) * 100
        return `${Math.round(percent * 10) / 10}%`
      } else {
        return ''
      }
    },
    getProductValueForDate: function(farmId, product, dateName) {
      var value = this.getChartData('mastitis_treatments')[farmId].filter(x => x['Date'] == dateName && x['Product'] == product)
      if (value.length > 0) {
        return value[0]['Cows Treated'];
      }
      return 0
    },
    chartMastitisTreatmentsProducts: function (farmId) {
      return Array.from(
        new Set(
          (this.getChartData('mastitis_treatments')[farmId] || []).filter((x) => x['Product']).map((x) => x['Product'])
        )
      )
    },
    treatmentsChartData: function(farmId) {
      var self = this;

      var dates  = this.chartMastitisTreatmentsDates(farmId)
      var products = this.chartMastitisTreatmentsProducts(farmId)

      var datasets = []

      products.forEach(function(productName, index) {
        var product = {
          label: productName,
          backgroundColor: self.colors[index % 10],  // index % 10 in case we iterate over more than 10 products
          data: [],
        }
        dates.forEach(function(dateName){
          product['data'].push(self.getProductValueForDate(farmId, productName, dateName))
        })

        datasets.push(product)
      })

      return {
        labels: dates,
        datasets: datasets
      }
    },
    chartMastitisTreatmentsDates: function (farmId) {
      return Array.from(
        new Set((this.getChartData('mastitis_treatments')[farmId] || []).map((x) => x['Date']))
      ).reverse()
    },
  },
};
</script>

<style>
.farm-fields {
  float: left;
  width: 350px;
  margin-right: 3em;
}
</style>
